/**
 * Alshallal Pastry Company - Email Broadcast Notifications Page
 * Phase 20: HR Email Notifications under Push Notifications
 * Phase 24.11: Email Analytics with Open Rate Tracking
 * 
 * Access: HR Manager, Super Admin
 * 
 * Features:
 * - Send email broadcasts to all or selected employees
 * - Optional future employee auto-delivery
 * - Attachment support
 * - Sent notifications list with delivery tracking
 * - Email analytics with open rates
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Mail, 
  Send, 
  Users, 
  UserCheck, 
  Paperclip, 
  X, 
  Loader2, 
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Download,
  Eye,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  TrendingUp,
  MailOpen,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status styling
const STATUS_STYLES = {
  draft: { bg: 'bg-slate-100 text-slate-700', icon: Clock },
  queued: { bg: 'bg-blue-100 text-blue-700', icon: Clock },
  sending: { bg: 'bg-amber-100 text-amber-700', icon: Loader2 },
  sent: { bg: 'bg-green-100 text-green-700', icon: CheckCircle },
  partial_failed: { bg: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  failed: { bg: 'bg-red-100 text-red-700', icon: XCircle },
};

const DELIVERY_STYLES = {
  pending: { bg: 'bg-slate-100 text-slate-700', icon: Clock },
  sent: { bg: 'bg-green-100 text-green-700', icon: CheckCircle },
  failed: { bg: 'bg-red-100 text-red-700', icon: XCircle },
  skipped: { bg: 'bg-slate-100 text-slate-600', icon: AlertTriangle },
};

const NotificationsPage = () => {
  const { token, hasRole } = useAuth();
  const { isRTL } = useLanguage();
  const fileInputRef = useRef(null);

  // Form state
  const [audienceType, setAudienceType] = useState('all');
  const [includeFutureEmployees, setIncludeFutureEmployees] = useState(true);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  // Employee search
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);

  // Broadcasts list
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Details dialog
  const [detailsDialog, setDetailsDialog] = useState({ open: false, broadcast: null });
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesPage, setDeliveriesPage] = useState(1);
  const [deliveriesTotalPages, setDeliveriesTotalPages] = useState(1);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveriesFilter, setDeliveriesFilter] = useState('all');

  // RBAC check
  const canAccess = hasRole('hr_manager', 'super_admin');

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [broadcastAnalytics, setBroadcastAnalytics] = useState(null);
  const [broadcastAnalyticsLoading, setBroadcastAnalyticsLoading] = useState(false);

  // Fetch analytics summary
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [token]);

  // Fetch analytics for a specific broadcast
  const fetchBroadcastAnalytics = useCallback(async (broadcastId) => {
    setBroadcastAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications/analytics/broadcast/${broadcastId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroadcastAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch broadcast analytics:', err);
    } finally {
      setBroadcastAnalyticsLoading(false);
    }
  }, [token]);

  // Fetch broadcasts
  const fetchBroadcasts = useCallback(async () => {
    setBroadcastsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications/email-broadcast`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, page_size: 10 }
      });
      setBroadcasts(res.data.broadcasts || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
    } finally {
      setBroadcastsLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    if (canAccess) {
      fetchBroadcasts();
      fetchAnalytics();
    }
  }, [fetchBroadcasts, fetchAnalytics, canAccess]);

  // Search employees
  const searchEmployees = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setEmployeeSearchResults([]);
      return;
    }
    
    setSearchingEmployees(true);
    try {
      // Use dedicated recipients endpoint for better search
      const res = await axios.get(`${API_URL}/api/notifications/recipients`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { query, limit: 20 }
      });
      setEmployeeSearchResults(res.data.recipients || []);
    } catch (err) {
      console.error('Failed to search recipients:', err);
      // Fallback to employees endpoint
      try {
        const fallbackRes = await axios.get(`${API_URL}/api/employees`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: query, page_size: 20 }
        });
        setEmployeeSearchResults(fallbackRes.data.employees || []);
      } catch (fallbackErr) {
        console.error('Fallback search also failed:', fallbackErr);
      }
    } finally {
      setSearchingEmployees(false);
    }
  }, [token]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (employeeSearch) {
        searchEmployees(employeeSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearch, searchEmployees]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg'];
    
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (file.size > maxSize) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return false;
      }
      if (!allowedTypes.includes(ext)) {
        toast.error(`${file.name} has unsupported file type`);
        return false;
      }
      return true;
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
    e.target.value = ''; // Reset input
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Add employee to selection
  const addEmployee = (emp) => {
    if (!selectedEmployees.find(e => e.id === emp.id)) {
      setSelectedEmployees(prev => [...prev, emp]);
    }
    setEmployeeSearch('');
    setEmployeeSearchResults([]);
  };

  // Remove employee from selection
  const removeEmployee = (empId) => {
    setSelectedEmployees(prev => prev.filter(e => e.id !== empId));
  };

  // Send broadcast
  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error(isRTL ? 'الموضوع مطلوب' : 'Subject is required');
      return;
    }
    if (!message.trim()) {
      toast.error(isRTL ? 'الرسالة مطلوبة' : 'Message is required');
      return;
    }
    if (audienceType === 'selected' && selectedEmployees.length === 0) {
      toast.error(isRTL ? 'اختر موظفًا واحدًا على الأقل' : 'Select at least one employee');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('message', message);
      formData.append('audience_type', audienceType);
      formData.append('include_future_employees', audienceType === 'all' ? includeFutureEmployees : false);
      
      if (audienceType === 'selected') {
        formData.append('selected_employee_ids', JSON.stringify(selectedEmployees.map(e => e.id)));
      }
      
      attachments.forEach(file => {
        formData.append('files', file);
      });

      const res = await axios.post(`${API_URL}/api/notifications/email-broadcast`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(isRTL 
        ? `تم إرسال البث إلى ${res.data.total_recipients} مستلم`
        : `Broadcast sent to ${res.data.total_recipients} recipients`
      );

      // Reset form
      setSubject('');
      setMessage('');
      setSelectedEmployees([]);
      setAttachments([]);
      setAudienceType('all');
      setIncludeFutureEmployees(true);
      
      // Refresh list
      fetchBroadcasts();

    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      toast.error(isRTL ? `فشل الإرسال: ${errorMsg}` : `Send failed: ${errorMsg}`);
    } finally {
      setSending(false);
    }
  };

  // Fetch deliveries for details dialog
  const fetchDeliveries = async (broadcastId, statusFilter = 'all') => {
    setDeliveriesLoading(true);
    try {
      const params = { page: deliveriesPage, page_size: 20 };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const res = await axios.get(`${API_URL}/api/notifications/email-broadcast/${broadcastId}/deliveries`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setDeliveries(res.data.deliveries || []);
      setDeliveriesTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch deliveries:', err);
    } finally {
      setDeliveriesLoading(false);
    }
  };

  // Open details dialog
  const openDetails = async (broadcast) => {
    setDetailsDialog({ open: true, broadcast });
    setDeliveriesPage(1);
    setDeliveriesFilter('all');
    await fetchDeliveries(broadcast.id);
  };

  // Effect for deliveries pagination/filter
  useEffect(() => {
    if (detailsDialog.open && detailsDialog.broadcast) {
      fetchDeliveries(detailsDialog.broadcast.id, deliveriesFilter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveriesPage, deliveriesFilter]);

  // Download attachment
  const downloadAttachment = async (attachmentId, fileName) => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/attachments/${attachmentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  // RBAC guard
  if (!canAccess) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">
          {isRTL ? 'غير مصرح' : 'Access Denied'}
        </h2>
        <p className="text-slate-600">
          {isRTL 
            ? 'يتطلب دور مدير الموارد البشرية أو المسؤول الأعلى'
            : 'Requires HR Manager or Super Admin role'}
        </p>
      </div>
    );
  }

  const isFormValid = subject.trim() && message.trim() && 
    (audienceType === 'all' || selectedEmployees.length > 0);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              {isRTL ? 'البث عبر البريد الإلكتروني' : 'Email Broadcast'}
            </h1>
          </div>
          <p className="text-slate-600 text-sm mt-1">
            {isRTL 
              ? 'إرسال إشعارات البريد الإلكتروني لجميع الموظفين أو مجموعة محددة'
              : 'Send email notifications to all employees or a selected group'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={showAnalytics ? "default" : "outline"}
            size="sm" 
            onClick={() => setShowAnalytics(!showAnalytics)}
          >
            <BarChart3 className="h-4 w-4 me-2" />
            {isRTL ? 'التحليلات' : 'Analytics'}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { fetchBroadcasts(); fetchAnalytics(); }}
            disabled={broadcastsLoading}
          >
            <RefreshCw className={cn("h-4 w-4 me-2", broadcastsLoading && "animate-spin")} />
            {isRTL ? 'تحديث' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="space-y-4">
          {/* Analytics Summary Cards */}
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : analytics && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500 rounded-lg">
                        <Mail className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-700">{analytics.total_broadcasts}</p>
                        <p className="text-xs text-blue-600">{isRTL ? 'إجمالي البث' : 'Total Broadcasts'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <Send className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-700">{analytics.total_emails_sent}</p>
                        <p className="text-xs text-green-600">{isRTL ? 'رسائل مرسلة' : 'Emails Sent'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-500 rounded-lg">
                        <MailOpen className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-700">{analytics.total_emails_opened}</p>
                        <p className="text-xs text-purple-600">{isRTL ? 'رسائل مفتوحة' : 'Emails Opened'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-500 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-700">{analytics.overall_open_rate}%</p>
                        <p className="text-xs text-amber-600">{isRTL ? 'معدل الفتح' : 'Open Rate'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Performance Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      {isRTL ? 'معدلات الأداء' : 'Performance Rates'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">{isRTL ? 'معدل التسليم' : 'Delivery Rate'}</span>
                          <span className="font-medium text-green-600">{analytics.overall_delivery_rate}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-500" 
                            style={{ width: `${analytics.overall_delivery_rate}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">{isRTL ? 'معدل الفتح' : 'Open Rate'}</span>
                          <span className="font-medium text-purple-600">{analytics.overall_open_rate}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-purple-500 h-2 rounded-full transition-all duration-500" 
                            style={{ width: `${analytics.overall_open_rate}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                      {isRTL 
                        ? `${analytics.broadcasts_this_month} بث هذا الشهر`
                        : `${analytics.broadcasts_this_month} broadcasts this month`}
                    </p>
                  </CardContent>
                </Card>

                {/* Top Performing Broadcast */}
                {analytics.top_performing_broadcast && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-500" />
                        {isRTL ? 'أفضل أداء' : 'Top Performing'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="font-medium text-sm truncate">{analytics.top_performing_broadcast.subject}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-600">
                            <MailOpen className="h-4 w-4 inline me-1" />
                            {analytics.top_performing_broadcast.open_rate}% {isRTL ? 'فتح' : 'opened'}
                          </span>
                          <span className="text-slate-500">
                            {analytics.top_performing_broadcast.opened_count}/{analytics.top_performing_broadcast.sent_count} {isRTL ? 'رسالة' : 'emails'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">
                          {formatDate(analytics.top_performing_broadcast.created_at)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Recent Broadcasts Performance */}
              {analytics.recent_broadcasts?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{isRTL ? 'أداء البث الأخير' : 'Recent Broadcast Performance'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analytics.recent_broadcasts.slice(0, 5).map((bc) => (
                        <div key={bc.broadcast_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{bc.subject}</p>
                            <p className="text-xs text-slate-500">
                              {bc.sent_count} {isRTL ? 'مرسل' : 'sent'} • {formatDate(bc.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm font-semibold text-purple-600">{bc.open_rate}%</p>
                              <p className="text-xs text-slate-400">{isRTL ? 'فتح' : 'opened'}</p>
                            </div>
                            <div className="w-16 bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-purple-500 h-2 rounded-full" 
                                style={{ width: `${bc.open_rate}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Composer Card */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Send className="h-5 w-5" />
              {isRTL ? 'رسالة جديدة' : 'New Message'}
            </CardTitle>
            <CardDescription>
              {isRTL ? 'إنشاء وإرسال بث بريد إلكتروني' : 'Compose and send an email broadcast'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Audience Selection */}
            <div className="space-y-2">
              <Label>{isRTL ? 'الجمهور' : 'Audience'}</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={audienceType === 'all' ? 'default' : 'outline'}
                  onClick={() => setAudienceType('all')}
                  className="flex-1"
                >
                  <Users className="h-4 w-4 me-2" />
                  {isRTL ? 'جميع الموظفين' : 'All Employees'}
                </Button>
                <Button
                  type="button"
                  variant={audienceType === 'selected' ? 'default' : 'outline'}
                  onClick={() => setAudienceType('selected')}
                  className="flex-1"
                >
                  <UserCheck className="h-4 w-4 me-2" />
                  {isRTL ? 'موظفين محددين' : 'Selected'}
                </Button>
              </div>
            </div>

            {/* Future Employees Toggle (only for All) */}
            {audienceType === 'all' && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">
                    {isRTL ? 'تضمين الموظفين المستقبليين' : 'Include Future Employees'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {isRTL 
                      ? 'إرسال تلقائي للموظفين الجدد المنضمين لاحقًا'
                      : 'Auto-send to new employees who join later'}
                  </p>
                </div>
                <Switch
                  checked={includeFutureEmployees}
                  onCheckedChange={setIncludeFutureEmployees}
                />
              </div>
            )}

            {/* Employee Search (only for Selected) */}
            {audienceType === 'selected' && (
              <div className="space-y-2">
                <Label>{isRTL ? 'اختر الموظفين' : 'Select Employees'}</Label>
                <div className="relative">
                  <Input
                    placeholder={isRTL ? 'البحث بالاسم أو البريد...' : 'Search by name or email...'}
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                  />
                  {searchingEmployees && (
                    <Loader2 className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {employeeSearchResults.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {employeeSearchResults.map(emp => (
                      <button
                        key={emp.id}
                        className="w-full px-3 py-2 text-start hover:bg-slate-50 border-b last:border-b-0"
                        onClick={() => addEmployee(emp)}
                      >
                        <p className="font-medium text-sm">
                          {emp.full_name || `${emp.first_name || emp.personal?.first_name || ''} ${emp.last_name || emp.personal?.last_name || ''}`.trim()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {emp.email || emp.personal?.work_email || emp.personal?.personal_email || 'No email'}
                        </p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected Employees */}
                {selectedEmployees.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedEmployees.map(emp => (
                      <Badge key={emp.id} variant="secondary" className="gap-1">
                        {emp.personal?.first_name} {emp.personal?.last_name}
                        <button onClick={() => removeEmployee(emp.id)}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label>{isRTL ? 'الموضوع' : 'Subject'} *</Label>
              <Input
                placeholder={isRTL ? 'أدخل موضوع البريد' : 'Enter email subject'}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label>{isRTL ? 'الرسالة' : 'Message'} *</Label>
              <Textarea
                placeholder={isRTL ? 'أدخل نص الرسالة...' : 'Enter message content...'}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
              />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Label>{isRTL ? 'المرفقات' : 'Attachments'}</Label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
              >
                <Paperclip className="h-4 w-4 me-2" />
                {isRTL ? 'إضافة مرفقات' : 'Add Attachments'}
              </Button>
              <p className="text-xs text-slate-500">
                {isRTL 
                  ? 'الحد الأقصى 10MB لكل ملف. PDF, DOC, XLS, PNG, JPG'
                  : 'Max 10MB per file. PDF, DOC, XLS, PNG, JPG'}
              </p>
              
              {/* Attachment List */}
              {attachments.length > 0 && (
                <div className="space-y-1 mt-2">
                  {attachments.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded">
                      <span className="text-sm truncate flex-1">{file.name}</span>
                      <button onClick={() => removeAttachment(idx)} className="text-slate-400 hover:text-red-500">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Send Button */}
            <Button 
              className="w-full" 
              onClick={handleSend}
              disabled={!isFormValid || sending}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 me-2" />
              )}
              {isRTL ? 'إرسال الآن' : 'Send Now'}
            </Button>
          </CardContent>
        </Card>

        {/* Sent Broadcasts Card */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {isRTL ? 'الإشعارات المرسلة' : 'Sent Notifications'}
            </CardTitle>
            <CardDescription>
              {isRTL ? 'سجل البث السابقة وحالتها' : 'History of sent broadcasts and their status'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {broadcastsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : broadcasts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>{isRTL ? 'لا توجد إشعارات مرسلة بعد' : 'No broadcasts sent yet'}</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {broadcasts.map((b) => {
                    const statusStyle = STATUS_STYLES[b.status] || STATUS_STYLES.draft;
                    const StatusIcon = statusStyle.icon;
                    return (
                      <div
                        key={b.id}
                        className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                        onClick={() => openDetails(b)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{b.subject}</p>
                            <p className="text-xs text-slate-500">{formatDate(b.created_at)}</p>
                          </div>
                          <Badge className={statusStyle.bg}>
                            <StatusIcon className={cn("h-3 w-3 me-1", b.status === 'sending' && "animate-spin")} />
                            {b.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>
                            {isRTL ? 'الجمهور:' : 'Audience:'} {b.audience_type === 'all' ? (isRTL ? 'الكل' : 'All') : (isRTL ? 'محدد' : 'Selected')}
                          </span>
                          <span className="text-green-600">✓ {b.sent_count}</span>
                          {b.failed_count > 0 && <span className="text-red-600">✗ {b.failed_count}</span>}
                          {b.skipped_count > 0 && <span className="text-slate-400">⊘ {b.skipped_count}</span>}
                          {b.opened_count > 0 && (
                            <span className="text-purple-600">
                              <MailOpen className="h-3 w-3 inline me-1" />
                              {b.opened_count} {isRTL ? 'فتح' : 'opened'}
                            </span>
                          )}
                          {b.attachments?.length > 0 && (
                            <span><Paperclip className="h-3 w-3 inline" /> {b.attachments.length}</span>
                          )}
                        </div>
                        {b.include_future_employees && b.active_broadcast && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            {isRTL ? 'بث نشط للموظفين الجدد' : 'Active for new employees'}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-sm text-slate-500">
                      {isRTL ? `صفحة ${page} من ${totalPages}` : `Page ${page} of ${totalPages}`}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsDialog.open} onOpenChange={(open) => !open && setDetailsDialog({ open: false, broadcast: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              {isRTL ? 'تفاصيل البث' : 'Broadcast Details'}
            </DialogTitle>
            <DialogDescription>
              {detailsDialog.broadcast?.subject}
            </DialogDescription>
          </DialogHeader>

          {detailsDialog.broadcast && (
            <div className="space-y-4">
              {/* Broadcast Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">{isRTL ? 'الحالة' : 'Status'}</p>
                  <Badge className={STATUS_STYLES[detailsDialog.broadcast.status]?.bg}>
                    {detailsDialog.broadcast.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{isRTL ? 'تاريخ الإرسال' : 'Sent At'}</p>
                  <p className="text-sm font-medium">{formatDate(detailsDialog.broadcast.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{isRTL ? 'المرسل' : 'Sent By'}</p>
                  <p className="text-sm">{detailsDialog.broadcast.created_by_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{isRTL ? 'الجمهور' : 'Audience'}</p>
                  <p className="text-sm">{detailsDialog.broadcast.audience_type === 'all' ? 'All Employees' : 'Selected'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{isRTL ? 'إجمالي المستلمين' : 'Total Recipients'}</p>
                  <p className="text-sm font-medium">{detailsDialog.broadcast.total_recipients}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{isRTL ? 'الإحصائيات' : 'Stats'}</p>
                  <p className="text-sm">
                    <span className="text-green-600">✓ {detailsDialog.broadcast.sent_count}</span>
                    {' / '}
                    <span className="text-red-600">✗ {detailsDialog.broadcast.failed_count}</span>
                    {' / '}
                    <span className="text-slate-400">⊘ {detailsDialog.broadcast.skipped_count}</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{isRTL ? 'معدل الفتح' : 'Open Rate'}</p>
                  <p className="text-sm">
                    <span className="text-purple-600 font-medium">
                      <MailOpen className="h-3 w-3 inline me-1" />
                      {detailsDialog.broadcast.opened_count || 0} {isRTL ? 'فتح' : 'opened'}
                    </span>
                    {detailsDialog.broadcast.sent_count > 0 && (
                      <span className="text-slate-500 ms-2">
                        ({Math.round((detailsDialog.broadcast.opened_count || 0) / detailsDialog.broadcast.sent_count * 100)}%)
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Message Preview */}
              <div>
                <p className="text-sm font-medium mb-2">{isRTL ? 'الرسالة' : 'Message'}</p>
                <div className="p-3 bg-white border rounded-lg text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {detailsDialog.broadcast.message}
                </div>
              </div>

              {/* Attachments */}
              {detailsDialog.broadcast.attachments?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">{isRTL ? 'المرفقات' : 'Attachments'}</p>
                  <div className="space-y-1">
                    {detailsDialog.broadcast.attachments.map(att => (
                      <div key={att.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm">{att.file_name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadAttachment(att.id, att.file_name)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deliveries Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">{isRTL ? 'التسليم' : 'Deliveries'}</p>
                  <Select value={deliveriesFilter} onValueChange={(v) => { setDeliveriesFilter(v); setDeliveriesPage(1); }}>
                    <SelectTrigger className="w-[140px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                      <SelectItem value="sent">{isRTL ? 'مرسل' : 'Sent'}</SelectItem>
                      <SelectItem value="failed">{isRTL ? 'فشل' : 'Failed'}</SelectItem>
                      <SelectItem value="skipped">{isRTL ? 'تم تخطيه' : 'Skipped'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {deliveriesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table className="min-w-[500px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                            <TableHead>{isRTL ? 'البريد' : 'Email'}</TableHead>
                            <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                            <TableHead>{isRTL ? 'فتح' : 'Opened'}</TableHead>
                            <TableHead>{isRTL ? 'التاريخ' : 'Date'}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deliveries.map(d => {
                            const dStyle = DELIVERY_STYLES[d.status] || DELIVERY_STYLES.pending;
                            const DIcon = dStyle.icon;
                            return (
                              <TableRow key={d.id}>
                                <TableCell className="font-medium">
                                  {d.employee_name || '-'}
                                  {d.is_future_employee && (
                                    <Badge variant="outline" className="ms-2 text-xs">New</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-slate-500 text-sm">{d.email || '-'}</TableCell>
                                <TableCell>
                                  <Badge className={dStyle.bg}>
                                    <DIcon className="h-3 w-3 me-1" />
                                    {d.status}
                                  </Badge>
                                  {d.error_message && (
                                    <p className="text-xs text-red-500 mt-1">{d.error_message}</p>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {d.opened ? (
                                    <Badge className="bg-purple-100 text-purple-700">
                                      <MailOpen className="h-3 w-3 me-1" />
                                      {d.open_count || 1}x
                                    </Badge>
                                  ) : (
                                    <span className="text-slate-400 text-xs">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-xs text-slate-500">
                                  {d.sent_at ? formatDate(d.sent_at) : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Deliveries Pagination */}
                    {deliveriesTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t">
                        <span className="text-xs text-slate-500">
                          Page {deliveriesPage} of {deliveriesTotalPages}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeliveriesPage(p => Math.max(1, p - 1))}
                            disabled={deliveriesPage === 1}
                          >
                            <ChevronLeft className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeliveriesPage(p => Math.min(deliveriesTotalPages, p + 1))}
                            disabled={deliveriesPage === deliveriesTotalPages}
                          >
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialog({ open: false, broadcast: null })}>
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsPage;
